import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Crown, Upload, Target, BarChart3, Lock, Rocket, CheckCircle, AlertTriangle,
  Loader2, Send, Music, Clock, Sparkles,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = { upload: Upload, target: Target, chart: BarChart3, lock: Lock, rocket: Rocket };

const UltimatePackPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [guide, setGuide] = useState(null);
  const [requests, setRequests] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    genre: '',
    style_description: '',
    reference_artists: '',
    reference_songs: '',
    additional_notes: '',
  });
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [gRes, rRes] = await Promise.all([
        axios.get(`${API_URL}/api/ultimate/distribution-guide`, { withCredentials: true }),
        axios.get(`${API_URL}/api/ultimate/my-requests`, { withCredentials: true }),
      ]);
      setGuide(gRes.data);
      setRequests(rRes.data);
    } catch (e) {
      if (e.response?.status === 403) setForbidden(true);
      else if (e.response?.status === 401) navigate('/login');
    }
    setLoading(false);
  };

  const submitRequest = async () => {
    if (!form.genre.trim() || !form.style_description.trim()) {
      alert('Genero y descripcion del estilo son requeridos');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/ultimate/style-request`, form, { withCredentials: true });
      await loadData();
      setFormOpen(false);
      setForm({ genre: '', style_description: '', reference_artists: '', reference_songs: '', additional_notes: '' });
      alert('Solicitud enviada. Comenzaremos a crear tus 50 canciones.');
    } catch (e) {
      alert(e.response?.data?.detail || 'Error enviando solicitud');
    }
    setSubmitting(false);
  };

  if (loading) {
    return <div className="min-h-screen pt-32 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#E8720C]" /></div>;
  }

  if (forbidden) {
    return (
      <div className="min-h-screen pt-32 pb-20" data-testid="ultimate-forbidden">
        <div className="container-custom max-w-2xl text-center">
          <div className="w-16 h-16 rounded-2xl bg-[#E8720C]/15 flex items-center justify-center mx-auto mb-6">
            <Crown className="w-8 h-8 text-[#E8720C]" />
          </div>
          <h1 className="text-3xl font-bold mb-4">OG MUSIC Ultimate Pack</h1>
          <p className="text-white/60 mb-8">
            Este contenido es exclusivo para miembros Ultimate. Incluye 50 canciones creadas a tu medida,
            guia exclusiva de distribucion privada, tips secretos OG MUSIC y acceso al Ecosistema de Playlists.
          </p>
          <button onClick={() => navigate('/membership')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#E8720C] hover:bg-[#C55A00] text-white rounded-full font-bold transition-colors"
            data-testid="go-to-membership">
            <Crown className="w-4 h-4" /> Ver Ultimate Pack
          </button>
        </div>
      </div>
    );
  }

  const statusStyles = {
    pending: { label: 'Pendiente de iniciar', color: 'text-yellow-400 bg-yellow-400/10' },
    in_progress: { label: 'En produccion', color: 'text-orange-400 bg-orange-400/10' },
    delivered: { label: 'Entregado', color: 'text-green-400 bg-green-400/10' },
  };

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="ultimate-page">
      <div className="container-custom">
        {/* Hero */}
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E8720C]/15 text-[#E8720C] text-xs font-semibold mb-4 uppercase tracking-wider">
            <Crown className="w-3.5 h-3.5" /> Ultimate Member
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold mb-3">
            Tu <span className="text-[#E8720C]">Ultimate Pack</span>
          </h1>
          <p className="text-base text-white/60 max-w-2xl mx-auto">
            Solicita tus 50 canciones personalizadas, accede a la guia exclusiva de distribucion privada
            y descubre los tips OG MUSIC para explotar tu cuenta.
          </p>
        </div>

        {/* Style Request Section */}
        <div className="glass-card p-6 mb-10 border border-[#E8720C]/15" data-testid="style-request-section">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
            <div>
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#E8720C]" /> Solicitar mis 50 canciones
              </h2>
              <p className="text-xs text-white/50 mt-1">Dinos que genero y estilo quieres, y nuestro equipo las crea para ti.</p>
            </div>
            {requests.length === 0 && (
              <button onClick={() => setFormOpen(true)}
                className="px-4 py-2 bg-[#E8720C] hover:bg-[#C55A00] text-white rounded-lg text-sm font-bold flex items-center gap-2"
                data-testid="open-style-form">
                <Send className="w-4 h-4" /> Comenzar solicitud
              </button>
            )}
          </div>

          {/* Existing requests */}
          {requests.map((r) => {
            const s = statusStyles[r.status] || { label: r.status, color: 'text-white/50 bg-white/5' };
            return (
              <div key={r.id} className="p-4 rounded-xl bg-white/[0.02] border border-white/5 mb-3" data-testid={`request-${r.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                  <div>
                    <p className="text-sm font-semibold">{r.genre}</p>
                    <p className="text-[11px] text-white/40 flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(r.created_at).toLocaleDateString()}</p>
                  </div>
                  <span className={`px-2.5 py-1 rounded-md text-[11px] font-semibold ${s.color}`}>{s.label}</span>
                </div>
                <p className="text-xs text-white/60 italic mb-3">{r.style_description}</p>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="flex items-center justify-between text-[10px] text-white/40 mb-1">
                      <span>Progreso</span>
                      <span>{r.songs_delivered} / {r.total_songs}</span>
                    </div>
                    <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-[#E8720C] transition-all"
                        style={{ width: `${(r.songs_delivered / r.total_songs) * 100}%` }} />
                    </div>
                  </div>
                </div>
                {r.staff_notes && (
                  <p className="text-[11px] text-purple-300 mt-3 p-2 bg-purple-500/5 rounded border-l-2 border-purple-500/30">
                    Nota del equipo: {r.staff_notes}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* Distribution Guide */}
        {guide && (
          <div className="mb-10" data-testid="distribution-guide">
            <div className="text-center mb-8">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/15 text-purple-300 text-xs font-semibold mb-3 uppercase tracking-wider">
                <Lock className="w-3 h-3" /> Contenido exclusivo
              </span>
              <h2 className="text-3xl font-bold mb-3">{guide.title}</h2>
              <p className="text-sm text-white/60 max-w-3xl mx-auto">{guide.intro}</p>
            </div>

            <div className="space-y-5">
              {guide.sections.map((s, i) => {
                const Icon = iconMap[s.icon] || Music;
                return (
                  <div key={i} className="glass-card p-6 border border-white/5" data-testid={`guide-step-${i + 1}`}>
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 rounded-xl bg-[#E8720C]/15 flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5 text-[#E8720C]" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold mb-2">{s.title}</h3>
                        <p className="text-sm text-white/70 leading-relaxed mb-4">{s.content}</p>
                        {s.tips && s.tips.length > 0 && (
                          <div className="space-y-1.5 pl-4 border-l-2 border-[#E8720C]/30">
                            {s.tips.map((t, j) => (
                              <p key={j} className="text-xs text-white/60 flex items-start gap-2">
                                <CheckCircle className="w-3 h-3 text-[#E8720C] mt-0.5 shrink-0" /> {t}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {guide.important_note && (
              <div className="mt-6 p-5 rounded-2xl bg-[#E8720C]/5 border border-[#E8720C]/20 flex items-start gap-3" data-testid="guide-important-note">
                <AlertTriangle className="w-5 h-5 text-[#E8720C] shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-bold text-[#E8720C] uppercase tracking-wider mb-1">Importante</p>
                  <p className="text-sm text-white/80">{guide.important_note}</p>
                </div>
              </div>
            )}
            {guide.contact && (
              <p className="text-xs text-white/50 text-center mt-4">{guide.contact}</p>
            )}
          </div>
        )}
      </div>

      {/* Style form modal */}
      {formOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setFormOpen(false)}>
          <div className="glass-card p-6 w-full max-w-lg border border-white/10 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-1 flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#E8720C]" /> Describe tu estilo</h3>
            <p className="text-xs text-white/50 mb-4">Cuanto mas detalle nos des, mejor se va a ajustar a lo que buscas.</p>
            <div className="space-y-3 mb-4">
              <div>
                <label className="text-[11px] text-white/50 mb-1 block uppercase tracking-wider">Genero *</label>
                <input type="text" value={form.genre} onChange={e => setForm({ ...form, genre: e.target.value })}
                  placeholder="Ej: Reggaeton, Trap, Afrobeat, Pop Latino..."
                  className="w-full input-glass text-sm" data-testid="form-genre" />
              </div>
              <div>
                <label className="text-[11px] text-white/50 mb-1 block uppercase tracking-wider">Descripcion del estilo *</label>
                <textarea value={form.style_description} onChange={e => setForm({ ...form, style_description: e.target.value })}
                  placeholder="Ej: Reggaeton romantico con vibes oscuros, bajo pesado, melodias pegajosas..."
                  rows={3} className="w-full input-glass text-sm" data-testid="form-style" />
              </div>
              <div>
                <label className="text-[11px] text-white/50 mb-1 block uppercase tracking-wider">Artistas de referencia</label>
                <input type="text" value={form.reference_artists} onChange={e => setForm({ ...form, reference_artists: e.target.value })}
                  placeholder="Ej: Bad Bunny, Feid, Myke Towers"
                  className="w-full input-glass text-sm" data-testid="form-artists" />
              </div>
              <div>
                <label className="text-[11px] text-white/50 mb-1 block uppercase tracking-wider">Canciones de referencia</label>
                <input type="text" value={form.reference_songs} onChange={e => setForm({ ...form, reference_songs: e.target.value })}
                  placeholder="Ej: Titi me pregunto, Classy 101"
                  className="w-full input-glass text-sm" data-testid="form-songs" />
              </div>
              <div>
                <label className="text-[11px] text-white/50 mb-1 block uppercase tracking-wider">Notas adicionales</label>
                <textarea value={form.additional_notes} onChange={e => setForm({ ...form, additional_notes: e.target.value })}
                  placeholder="Ej: Preferencia por canciones de 3 min, no voces femeninas..."
                  rows={2} className="w-full input-glass text-sm" data-testid="form-notes" />
              </div>
            </div>
            <button onClick={submitRequest} disabled={submitting}
              className="w-full py-3 bg-[#E8720C] hover:bg-[#C55A00] text-white font-bold rounded-xl disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="submit-style-request">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Enviando...</> : <><Send className="w-4 h-4" /> Enviar solicitud</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UltimatePackPage;
